/**
 * AutomationScheduleSection — when this automation runs, as chips.
 *
 * Registered as `components.typeSpecific` for `ahoy_scheduled_automation` with
 * `typeSpecificPosition: 'before-overview'` — the schedule is the first
 * question a reader has about a scheduled automation, mirroring how
 * AutomationTriggersSection leads for event automations.
 *
 * Reads the `ahoy_schedule` property ({rrule, dtstart, tz}) and renders a
 * readable cadence chip plus the timezone. The rrule grammar is a backend
 * whitelist (RecurrenceService), so the light formatter here only needs to
 * cover those shapes; anything it does not recognise falls back to the raw
 * RRULE text rather than guessing. Self-gating: renders nothing when the
 * property is absent or malformed.
 */

import { useMemo } from 'react';

import { Badge } from '@/lib/base/badges/badges';
import { formatRrule } from '@/lib/crm/automations/formatRrule';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';

import { AutomationWebhookRows } from './automation-triggers-section';
import { CollapsibleSection } from './collapsible-section';

interface ScheduleValue {
  rrule: string;
  dtstart?: string;
  tz?: string;
}

function parseSchedule(value: unknown): ScheduleValue | null {
  if (typeof value !== 'object' || value === null) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (typeof record.rrule !== 'string' || record.rrule.length === 0) {
    return null;
  }
  return {
    rrule: record.rrule,
    dtstart: typeof record.dtstart === 'string' ? record.dtstart : undefined,
    tz: typeof record.tz === 'string' ? record.tz : undefined,
  };
}

export function AutomationScheduleSection({ object }: { object: CrmObject }) {
  const schedule = useMemo(
    () =>
      parseSchedule(
        getPropertyValue<unknown>(object.properties?.ahoy_schedule)
      ),
    [object]
  );

  if (schedule === null) {
    return null;
  }

  return (
    // CollapsibleSection (non-collapsible) for the same reason as the
    // triggers section: headers styled by shared code cannot drift apart.
    <CollapsibleSection title='Schedule' collapsible={false}>
      <div className='flex flex-col gap-2'>
        <div className='flex flex-wrap items-center gap-1.5'>
          <Badge type='pill-color' color='brand' size='sm'>
            {formatRrule(schedule.rrule)}
          </Badge>
          {schedule.tz !== undefined && (
            <Badge type='pill-color' color='gray' size='sm'>
              {schedule.tz}
            </Badge>
          )}
        </div>
        {/* Endpoint rows (outbound delivery target, if provisioned) — same
            up-top placement as the event automation's Triggers section; the
            association field row is registry-hidden. */}
        <AutomationWebhookRows object={object} />
      </div>
    </CollapsibleSection>
  );
}
